/**
 * Username validation rules aligned with et-auth constraints.
 *
 * Allowed characters: alphanumeric, underscore, period  (/^[a-z0-9_.]+$/i)
 * Minimum length: 2 (sig-auth config)
 * Maximum length: 32 (practical upper bound)
 * No leading/trailing whitespace
 * No reserved words
 */

const VALID_USERNAME_REGEX = /^[a-z0-9_.]+$/i;

const MIN_USERNAME_LENGTH = 2;
const MAX_USERNAME_LENGTH = 32;

const RESERVED_PATTERNS: RegExp[] = [
  /^admin/i,
  /^root/i,
  /^system/i,
  /^support/i,
  /^evertutor/i,
  /^null$/i,
  /^undefined$/i,
];

/**
 * Validate a username string.
 *
 * @param value      The raw input value.
 * @param checkEmpty When `true` (default / submit), empty fields produce an
 *                   error. When `false` (blur while typing), empty fields are
 *                   silently accepted so the user isn't nagged before they
 *                   start typing.
 * @returns An error message string, or `""` if valid.
 */
export const validateUsername = (
  value: string,
  checkEmpty = true,
): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return checkEmpty ? "Please enter a username." : "";
  }

  // Leading / trailing whitespace in the raw value
  if (value !== trimmed) {
    return "Username must not have leading or trailing spaces.";
  }

  if (trimmed.length < MIN_USERNAME_LENGTH) {
    return `Username must be at least ${MIN_USERNAME_LENGTH} characters.`;
  }

  if (trimmed.length > MAX_USERNAME_LENGTH) {
    return `Username must be at most ${MAX_USERNAME_LENGTH} characters.`;
  }

  if (!VALID_USERNAME_REGEX.test(trimmed)) {
    return "Username can only contain letters, numbers, underscores, and periods.";
  }

  for (const pattern of RESERVED_PATTERNS) {
    if (pattern.test(trimmed)) {
      return "This username is reserved. Please choose another.";
    }
  }

  return "";
};

export {
  VALID_USERNAME_REGEX,
  MIN_USERNAME_LENGTH,
  MAX_USERNAME_LENGTH,
  RESERVED_PATTERNS,
};
